/// <reference lib="dom" />
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import {
  TextInput as PaperTextInput,
  Button as PaperButton,
  HelperText,
} from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
// import Feather from '@react-native-vector-icons/feather/static'; // record-as-expense: goods receipt only
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  addSupplierInvoiceInformation,
  getPurchaseOrderInformation,
  getPurchaseOrdersInformation,
  getSuppliersInformation,
  parseCreatedInvoiceResponse,
  parsePurchaseOrderResponse,
  parsePurchaseOrdersResponse,
  parseSuppliersResponse,
} from '../../api/backofficeSuppliers';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  setInvoicesByPOIdData,
  setPurchaseOrderByIdData,
  setPurchaseOrdersBySupplierIdData,
  setPurchaseOrdersBySupplierIdError,
  setPurchaseOrdersBySupplierIdLoading,
  setSuppliersListData,
  upsertPurchaseOrderBySupplierId,
} from '../../redux/backoffice/suppliersSlice';
import { AppSnackbar } from '../../services/AppSnackbar';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import {
  formatNumericStringWithCommas,
  getCurrencyDisplayFormat,
  parseFormattedNumericString,
} from '../../utils/libs';
import { isPurchaseOrderCancelled } from '../../utils/purchaseOrderStatus';
import { getApiMessage } from '../../utils/apiError';
import DatePickerField from '../../components/Reusables/DatePickerField';
import FormSelectField from '../../components/Reusables/FormSelectField';
import SupplierPrerequisiteEmptyState from '../../components/suppliers/SupplierPrerequisiteEmptyState';
import type { RootStackParamList } from '../../navigation/AppStack';
import moment from 'moment';

type RouteParams = {
  supplierId?: string;
  purchaseOrderId?: string;
};

type LineItemPayload = { _id: string; quantity: number };

const MAX_WIDTH = 1440;
const MAX_HEIGHT = 1440;
const QUALITY = 0.75;

const resizeImageFileToJpegDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', QUALITY));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

const BackOfficeAddInvoiceScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { supplierId, purchaseOrderId } = (route.params || {}) as RouteParams;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();

  const suppliers = useAppSelector(
    state => state.backofficeSuppliers.list.items,
  );
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const [supplierIdSel, setSupplierIdSel] = useState(String(supplierId || ''));
  const [purchaseOrderIdSel, setPurchaseOrderIdSel] = useState(
    String(purchaseOrderId || ''),
  );
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(() => new Date());
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('0');
  const [notes, setNotes] = useState('');
  // const [createExpense, setCreateExpense] = useState(false); // record-as-expense: goods receipt only
  const [fileBase64, setFileBase64] = useState('');
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const previewBlobRef = useRef<string | null>(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [selectedPOLineItems, setSelectedPOLineItems] = useState<
    LineItemPayload[]
  >([]);
  const [saving, setSaving] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const purchaseOrdersCache = useAppSelector(
    state =>
      state.backofficeSuppliers.purchaseOrdersBySupplierId[
        String(supplierIdSel || '')
      ],
  );
  const purchaseOrders = Array.isArray(purchaseOrdersCache?.data)
    ? purchaseOrdersCache.data
    : [];
  const invoicesByPOEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.invoicesByPOId[
        String(purchaseOrderIdSel || '')
      ],
  );

  const supplierOptions = useMemo(
    () =>
      suppliers.map((supplier: any) => ({
        label: String(supplier?.bp_supplier_name || 'Unnamed supplier'),
        value: String(supplier?._id),
      })),
    [suppliers],
  );

  const purchaseOrderOptions = useMemo(
    () =>
      purchaseOrders.map((po: any) => ({
        label: String(
          po?.bp_purchase_order_number ||
            `Purchase order ${String(po?._id).slice(-6)}`,
        ),
        value: String(po?._id),
      })),
    [purchaseOrders],
  );

  const purchaseOrderPlaceholder = useMemo(() => {
    if (!supplierIdSel) return 'Select supplier first';
    return 'Select purchase order';
  }, [supplierIdSel]);

  const selectedPO = useMemo(
    () =>
      purchaseOrders.find(
        (po: any) => String(po?._id) === purchaseOrderIdSel,
      ) ?? null,
    [purchaseOrders, purchaseOrderIdSel],
  );
  const isSelectedPOCancelled = isPurchaseOrderCancelled(selectedPO);

  useEffect(() => {
    return () => {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setSupplierIdSel(String(supplierId || ''));
    setPurchaseOrderIdSel(String(purchaseOrderId || ''));
  }, [supplierId, purchaseOrderId]);

  useEffect(() => {
    if (!owner?.accessToken) return;
    if (suppliers.length > 0) return;
    setLoadingSuppliers(true);
    let mounted = true;

    getSuppliersInformation(axiosBackoffice as any, 1, 100, 'active', {
      accessToken: owner.accessToken,
    })
      .then(res => {
        setLoadingSuppliers(false);
        if (!mounted) return;
        const parsed = parseSuppliersResponse(res);
        if (parsed?.success && parsed.suppliers) {
          dispatch(
            setSuppliersListData({
              suppliers: parsed.suppliers,
              total: parsed.total ?? parsed.suppliers.length,
              page: parsed.page ?? 1,
              limit: parsed.limit ?? 100,
            }),
          );
        }
      })
      .catch((error: any) => {
        setLoadingSuppliers(false);
        if (!mounted) return;
        AppSnackbar.show(
          getApiMessage(error, 'Failed to load suppliers'),
          'error',
        );
      })
      .finally(() => {
        if (mounted) setLoadingSuppliers(false);
      });

    return () => {
      mounted = false;
    };
  }, [axiosBackoffice, dispatch, owner?.accessToken, suppliers.length]);

  useEffect(() => {
    if (!supplierIdSel || !owner?.accessToken) return;
    let mounted = true;

    dispatch(setPurchaseOrdersBySupplierIdLoading(supplierIdSel));
    getPurchaseOrdersInformation(
      axiosBackoffice as any,
      { supplierId: supplierIdSel },
      { accessToken: owner.accessToken },
    )
      .then(res => {
        if (!mounted) return;
        const parsed = parsePurchaseOrdersResponse(res);
        if (parsed?.success) {
          dispatch(
            setPurchaseOrdersBySupplierIdData({
              id: supplierIdSel,
              data: parsed.purchaseOrders || [],
            }),
          );
        } else {
          dispatch(
            setPurchaseOrdersBySupplierIdError({
              id: supplierIdSel,
              error: parsed?.message || 'Failed to load purchase orders',
            }),
          );
        }
      })
      .catch((error: any) => {
        if (!mounted) return;
        dispatch(
          setPurchaseOrdersBySupplierIdError({
            id: supplierIdSel,
            error: getApiMessage(error, 'Failed to load purchase orders'),
          }),
        );
      });

    return () => {
      mounted = false;
    };
  }, [axiosBackoffice, dispatch, owner?.accessToken, supplierIdSel]);

  useEffect(() => {
    if (!purchaseOrderIdSel || !owner?.accessToken) {
      setSelectedPOLineItems([]);
      return;
    }

    let mounted = true;
    getPurchaseOrderInformation(axiosBackoffice as any, purchaseOrderIdSel, {
      accessToken: owner.accessToken,
    })
      .then(res => {
        if (!mounted) return;
        const parsed = parsePurchaseOrderResponse(res);
        if (!parsed?.success || !parsed.purchaseOrder) {
          setSelectedPOLineItems([]);
          return;
        }

        const items =
          parsed.purchaseOrder?.bp_purchase_order_line_items ??
          parsed.purchaseOrder?.lineItems ??
          [];

        const lines: LineItemPayload[] = Array.isArray(items)
          ? items
              .filter((line: any) => Boolean(line?._id || line?.lineId))
              .map((line: any) => ({
                _id: String(line?._id || line?.lineId || ''),
                quantity:
                  Number(
                    line?.bp_line_item_quantity ??
                      line?.bp_line_item_mass ??
                      line?.quantity ??
                      0,
                  ) || 0,
              }))
          : [];

        setSelectedPOLineItems(lines);
      })
      .catch(() => {
        if (!mounted) return;
        setSelectedPOLineItems([]);
      });

    return () => {
      mounted = false;
    };
  }, [axiosBackoffice, owner?.accessToken, purchaseOrderIdSel]);

  const totalAmountNumber = Number(
    parseFormattedNumericString(totalAmount) || 0,
  );
  const canSubmit =
    Boolean(supplierIdSel) &&
    Boolean(purchaseOrderIdSel) &&
    totalAmountNumber > 0 &&
    !saving &&
    !isSelectedPOCancelled;

  const triggerFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const clearAttachment = useCallback(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    setPreviewObjectUrl(null);
    setFileBase64('');
  }, []);

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        AppSnackbar.show('Please choose an image file', 'error');
        return;
      }

      setImageProcessing(true);
      let blobUrl: string | null = null;
      try {
        if (previewBlobRef.current) {
          URL.revokeObjectURL(previewBlobRef.current);
        }
        blobUrl = URL.createObjectURL(file);
        previewBlobRef.current = blobUrl;
        setPreviewObjectUrl(blobUrl);
        const dataUrl = await resizeImageFileToJpegDataUrl(file);
        setFileBase64(dataUrl);
        URL.revokeObjectURL(blobUrl);
        previewBlobRef.current = null;
        setPreviewObjectUrl(null);
      } catch (err: any) {
        AppSnackbar.show(
          getApiMessage(err, 'Failed to process image'),
          'error',
        );
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
        if (previewBlobRef.current === blobUrl) {
          previewBlobRef.current = null;
        }
        setPreviewObjectUrl(null);
        setFileBase64('');
      } finally {
        setImageProcessing(false);
      }
    },
    [],
  );

  const handleSubmit = async () => {
    if (!canSubmit || !owner?.accessToken) return;
    setSaving(true);
    try {
      const payload: any = {
        bp_supplier_invoice_supplier_id: supplierIdSel,
        bp_supplier_invoice_purchase_order_id: purchaseOrderIdSel,
        bp_supplier_invoice_number: invoiceNumber.trim() || undefined,
        bp_supplier_invoice_date: invoiceDate
          ? moment(invoiceDate).format('YYYY-MM-DD')
          : undefined,
        bp_supplier_invoice_due_date: dueDate
          ? moment(dueDate).format('YYYY-MM-DD')
          : undefined,
        bp_supplier_invoice_total_amount: totalAmountNumber,
        bp_supplier_invoice_tax_amount: Number(
          parseFormattedNumericString(taxAmount) || 0,
        ),
        bp_supplier_invoice_notes: notes.trim() || undefined,
        // createExpense, // record-as-expense: goods receipt only
      };

      if (fileBase64) {
        payload.bp_supplier_invoice_file = fileBase64;
      }
      if (selectedPOLineItems.length > 0) {
        payload.bp_supplier_invoice_line_items = selectedPOLineItems.map(
          line => ({
            bp_invoice_line_po_line_id: line._id,
            bp_invoice_line_quantity: line.quantity,
          }),
        );
      }

      const res = await addSupplierInvoiceInformation(
        axiosBackoffice as any,
        payload,
        { accessToken: owner.accessToken },
      );
      const ok = res?.data?.success === true || res?.data?.status === 200;
      AppSnackbar.show(
        String(
          res?.data?.message ||
            (ok ? 'Invoice added successfully' : 'Failed to add invoice'),
        ),
        ok ? 'success' : 'error',
      );
      if (!ok) return;
      appAnalytics.track('procurement_invoice_created', {
        ...buildAppAnalyticsContext(),
        line_item_count: selectedPOLineItems.length,
        total_amount: totalAmountNumber,
      });
      const parsedCreate = parseCreatedInvoiceResponse(res);
      const createdInvoice = parsedCreate?.invoice;
      const createdPO = parsedCreate?.purchaseOrder;
      const resolvedPOId = String(purchaseOrderId || purchaseOrderIdSel || '');

      if (createdInvoice && resolvedPOId) {
        const currentInvoices = Array.isArray(invoicesByPOEntity?.data)
          ? invoicesByPOEntity.data
          : [];
        const createdInvoiceId = String(createdInvoice?._id || '');
        const mergedInvoices = createdInvoiceId
          ? [
              createdInvoice,
              ...currentInvoices.filter(
                (item: any) => String(item?._id || '') !== createdInvoiceId,
              ),
            ]
          : [createdInvoice, ...currentInvoices];
        dispatch(
          setInvoicesByPOIdData({
            id: resolvedPOId,
            data: mergedInvoices,
          }),
        );
      }

      if (createdPO && typeof createdPO === 'object') {
        const resolvedPOEntityId = String(createdPO?._id || resolvedPOId);
        dispatch(
          setPurchaseOrderByIdData({
            id: resolvedPOEntityId,
            data: createdPO,
          }),
        );
        const resolvedSupplierId = String(
          (typeof createdPO.bp_purchase_order_supplier_id === 'object'
            ? createdPO.bp_purchase_order_supplier_id?._id
            : createdPO.bp_purchase_order_supplier_id) ||
            supplierId ||
            supplierIdSel ||
            '',
        );
        if (resolvedSupplierId) {
          dispatch(
            upsertPurchaseOrderBySupplierId({
              supplierId: resolvedSupplierId,
              order: createdPO,
            }),
          );
        }
      }

      navigation.replace('BackOfficePurchaseOrderDetails', {
        supplierId: String(supplierId || supplierIdSel || ''),
        purchaseOrderId: String(purchaseOrderId || purchaseOrderIdSel || ''),
      });
    } catch (error: any) {
      AppSnackbar.show(getApiMessage(error, 'Failed to add invoice'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const displayImageUri = fileBase64 || previewObjectUrl || '';

  return (
    <View style={styles.container}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange as any}
        style={{ display: 'none' }}
      />
      <KeyboardAwareFormScroll
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        //  baseBottomPadding={v(80)}
        bottomOffset={v(240)}>
        <Text style={styles.sectionTitle}>Supplier Invoice Details</Text>
        {!loadingSuppliers && supplierOptions.length === 0 && !supplierIdSel ? (
          <SupplierPrerequisiteEmptyState
            description="Add a supplier before recording an invoice."
            onAddSupplier={() => navigation.navigate('BackOfficeAddSupplier')}
          />
        ) : (
          <FormSelectField
            label="Supplier *"
            value={supplierIdSel}
            onValueChange={value => {
              setSupplierIdSel(String(value || ''));
              setPurchaseOrderIdSel('');
            }}
            options={supplierOptions}
            placeholder="Select supplier"
            loading={loadingSuppliers}
          />
        )}

        <FormSelectField
          label="Purchase Order *"
          value={purchaseOrderIdSel}
          onValueChange={value => setPurchaseOrderIdSel(String(value || ''))}
          options={purchaseOrderOptions}
          disabled={!supplierIdSel}
          loading={!!purchaseOrdersCache?.loading}
          placeholder={purchaseOrderPlaceholder}
        />
        {isSelectedPOCancelled ? (
          <HelperText type="error" visible>
            This purchase order is cancelled and cannot be used.
          </HelperText>
        ) : null}

        <PaperTextInput
          mode="outlined"
          label="Enter invoice number"
          value={invoiceNumber}
          onChangeText={text => setInvoiceNumber(text.toUpperCase())}
          autoCapitalize="characters"
          placeholder="INV-001"
          style={styles.input}
        />

        <DatePickerField
          value={invoiceDate}
          onChange={setInvoiceDate}
          label="Invoice Date"
          displayFormat="iso"
          placeholder="Select date"
        />

        <DatePickerField
          value={dueDate}
          onChange={setDueDate}
          label="Due Date"
          placeholder="Select due date"
          optional
          displayFormat="iso"
        />

        <Text style={styles.sectionTitle}>Amounts</Text>
        <PaperTextInput
          mode="outlined"
          label={`Total Amount (${currencyText}) *`}
          keyboardType="numeric"
          value={totalAmount}
          onChangeText={text =>
            setTotalAmount(
              formatNumericStringWithCommas(parseFormattedNumericString(text)),
            )
          }
          placeholder="0.00"
          style={styles.input}
        />

        <PaperTextInput
          mode="outlined"
          label={`Tax Amount (${currencyText})`}
          keyboardType="numeric"
          value={taxAmount}
          onChangeText={text =>
            setTaxAmount(
              formatNumericStringWithCommas(parseFormattedNumericString(text)),
            )
          }
          placeholder="0"
          style={styles.input}
        />

        <Text style={styles.sectionTitle}>Notes</Text>
        <PaperTextInput
          mode="outlined"
          label="Notes"
          multiline
          value={notes}
          onChangeText={setNotes}
          placeholder="Optional notes"
          style={[styles.input, { height: v(100) }]}
        />

        {/* Record as expense — goods receipt only
        <TouchableOpacity
          style={styles.checkboxRow}
          onPress={() => setCreateExpense(prev => !prev)}
          activeOpacity={0.7}>
          <Feather
            name={createExpense ? 'check-square' : 'square'}
            size={fs(20)}
            color={
              createExpense ? theme.colors.primary : theme.colors.textSecondary
            }
          />
          <Text style={styles.checkboxLabel}>
            Record as expense
          </Text>
        </TouchableOpacity>
        <Text style={styles.checkboxHelperText}>
          Check this to also log this invoice amount as a business expense.
        </Text>
        */}

        <View>
          <Text style={styles.sectionTitle}>Attachment</Text>
          <Text style={styles.label}>Invoice Image (optional)</Text>
          <View style={styles.imagePickerRow}>
            <TouchableOpacity
              style={styles.imagePickerButton}
              onPress={triggerFilePicker}
              disabled={imageProcessing}>
              <Text style={styles.imagePickerLabel}>
                {imageProcessing
                  ? 'Processing...'
                  : fileBase64
                    ? 'Change image'
                    : 'Choose image'}
              </Text>
            </TouchableOpacity>
            {fileBase64 ? (
              <TouchableOpacity
                style={[
                  styles.imagePickerButton,
                  styles.imagePickerButtonMuted,
                ]}
                onPress={clearAttachment}
                disabled={imageProcessing}>
                <Text
                  style={[
                    styles.imagePickerLabel,
                    styles.imagePickerLabelMuted,
                  ]}>
                  Remove
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {displayImageUri ? (
            <Image
              source={{ uri: displayImageUri }}
              style={styles.previewImage}
            />
          ) : null}
        </View>
      </KeyboardAwareFormScroll>

      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <View style={styles.actions}>
            <PaperButton
              mode="outlined"
              onPress={() => navigation.goBack()}
              disabled={saving}
              style={styles.actionButton}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              labelStyle={styles.actionLabel}
              textColor={theme.colors.textSecondary}
              theme={{ colors: { outline: theme.colors.border } }}
              uppercase={false}>
              Cancel
            </PaperButton>
            <PaperButton
              mode="contained"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={saving}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              buttonColor={theme.colors.successDark}
              textColor={theme.colors.textInverse}
              uppercase={false}>
              {saving ? 'Saving...' : 'Add Invoice'}
            </PaperButton>
          </View>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    scroll: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      alignSelf: 'center',
      width: '100%',
    },
    content: {
      padding: s(24),
      gap: v(20),
      paddingBottom: v(24),
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(18),
      color: theme.colors.textDark,
      marginVertical: v(6),
      marginBottom: v(12),
      paddingBottom: v(8),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderGray,
    },
    label: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginBottom: v(4),
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(56),
      marginBottom: v(6),
      borderRadius: theme.borderRadius.md,
    },
    imagePickerRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(8),
    },
    // checkboxRow: {
    //   flexDirection: 'row',
    //   alignItems: 'center',
    //   marginTop: v(12),
    // },
    // checkboxLabel: {
    //   fontFamily: GTFonts.bold,
    //   fontSize: fs(14),
    //   lineHeight: fs(18),
    //   color: theme.colors.text,
    //   marginLeft: s(8),
    // },
    // checkboxHelperText: {
    //   fontFamily: GTFonts.regular,
    //   fontSize: fs(12),
    //   lineHeight: fs(16),
    //   color: theme.colors.textSecondary,
    //   marginLeft: s(28),
    //   marginTop: v(-4),
    // },
    imagePickerButton: {
      paddingHorizontal: s(12),
      paddingVertical: v(8),
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    imagePickerButtonMuted: {
      borderColor: theme.colors.borderGray,
    },
    imagePickerLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.primary,
    },
    imagePickerLabelMuted: {
      color: theme.colors.textSecondary,
    },
    previewImage: {
      marginTop: v(8),
      width: '100%',
      height: v(140),
      borderRadius: theme.borderRadius.md,
      resizeMode: 'contain',
      backgroundColor: theme.colors.surfaceVariant,
    },
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(24),
      paddingVertical: s(12),
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      alignSelf: 'center',
      width: '100%',
      borderTopWidth: 0,
    },
    actions: {
      flexDirection: 'row',
      gap: s(8),
    },
    actionButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
    },
    actionButtonContent: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
  });

export default BackOfficeAddInvoiceScreen;
